import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const isFree = searchParams.get('free') === 'true';
  const tier = searchParams.get('tier') || 'professional';
  const promoCode = searchParams.get('promo');

  useEffect(() => {
    // Just mark as loaded - the backend already updated the subscription
    // User can refresh manually or navigate to subscription page to see changes
    setLoading(false);
  }, []);

  const getTierName = (tierValue) => {
    const names = {
      'professional': 'Professional',
      'premium': 'Premium'
    };
    return names[tierValue] || tierValue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Processing your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Card className="border-2 border-green-200">
          <CardContent className="p-12">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="bg-green-100 rounded-full p-6">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
                <Sparkles className="h-8 w-8 text-yellow-500 absolute -top-2 -right-2 animate-pulse" />
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {isFree ? 'Free Subscription Activated!' : 'Subscription Successful!'}
              </h1>
              <p className="text-lg text-gray-600 mb-2">
                Welcome to {getTierName(tier)} tier!
              </p>
              {isFree && promoCode && (
                <div className="mt-4 inline-block">
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">Promo Code Applied:</span> {promoCode}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Benefits */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-center">Your Subscription Includes:</h3>
              <div className="space-y-3">
                {tier === 'premium' ? (
                  <>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Unlimited quote requests per month</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Featured listing on homepage</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Priority support</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Premium badge on your profile</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">25 quote requests per month</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Professional badge on your profile</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Email support</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>✓ Your subscription has been activated!</strong> To see your updated subscription status, please visit the Manage Subscription page below.
              </p>
            </div>

            {/* Next Steps */}
            <div className="space-y-3">
              <Button
                onClick={() => {
                  // Force full reload to ensure fresh data
                  window.location.href = '/subscription';
                  window.location.reload();
                }}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                size="lg"
              >
                View Updated Subscription
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/pro-quotes')}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Start Receiving Quotes
              </Button>
            </div>

            {/* Receipt Note */}
            {!isFree && (
              <p className="text-center text-sm text-gray-500 mt-6">
                A receipt has been sent to your email address.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Need help getting started?</p>
          <Button
            variant="link"
            onClick={() => navigate('/how-it-works')}
            className="text-orange-600 hover:text-orange-700"
          >
            View How It Works
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SubscriptionSuccessPage;
